/**
 * Storage URL utilities.
 *
 * The S3 bucket is private. Two access patterns exist:
 *
 * - Media (banners):   served via CloudFront at VITE_CLOUDFRONT_DOMAIN.
 *                      The DB stores the bare S3 key; this helper builds the URL.
 *
 * - Data uploads:      private, no CloudFront path. Callers must request a
 *                      short-lived presigned GET URL via GET /data-uploads/:id/presigned-url.
 */

/**
 * Builds a CloudFront URL for a media asset (e.g. banners).
 *
 * Handles both new-style keys ("assets/…") and legacy full S3/CloudFront URLs
 * so the app works correctly before and after the normalize-storage-keys migration.
 */
export function getMediaUrl(keyOrUrl: string | null | undefined): string | undefined {
	if (!keyOrUrl) return undefined;

	// A local preview of a file the user just picked; there is nothing to rewrite.
	if (keyOrUrl.startsWith('blob:')) return keyOrUrl;

	const domain = import.meta.env.VITE_CLOUDFRONT_DOMAIN;

	if (keyOrUrl.startsWith('http://') || keyOrUrl.startsWith('https://')) {
		// Rows written before storage keys were normalised hold the bucket's own
		// URL. Serving those directly bypasses the CDN and depends on the bucket
		// staying publicly readable, and the browser only gets them when the
		// bucket's CORS happens to list the current origin. Keep the key, swap the
		// host. Anything not on S3 (an external image someone pasted) is left alone.
		const key = mediaKeyFromS3Url(keyOrUrl);
		return key ? `https://${domain}/${key}` : keyOrUrl;
	}

	return `https://${domain}/${keyOrUrl}`;
}

/**
 * The object key inside a media bucket's own S3 URL, or null when the URL does
 * not point at one. Both the virtual-hosted style
 * ("https://<bucket>.s3.<region>.amazonaws.com/<key>") and the path style
 * ("https://s3.<region>.amazonaws.com/<bucket>/<key>") appear in old rows.
 */
function mediaKeyFromS3Url(url: string): string | null {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return null;
	}
	if (!parsed.hostname.endsWith('.amazonaws.com')) return null;

	const path = decodeURIComponent(parsed.pathname).replace(/^\//, '');
	// Path style: the first segment is the bucket name.
	if (parsed.hostname.startsWith('s3.') || parsed.hostname.startsWith('s3-')) {
		const [, ...rest] = path.split('/');
		return rest.length > 0 ? rest.join('/') : null;
	}
	return path || null;
}
